import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { formatSeconds } from "@/format/time";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { Duration, type PartialMessage } from "@bufbuild/protobuf";
import { type OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC, useCallback, useMemo } from "react";

type TimeoutOption = {
    label: string;
    value: number; // seconds
};

type MaximumTimeoutProps = {
    editable?: boolean;
};

export const MaximumTimeout: FC<MaximumTimeoutProps> = ({ editable = true }) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const currentTimeout = useMemo(() => {
        const seconds = organizationPolicies?.maximumEnvironmentTimeout?.seconds;
        return typeof seconds === "bigint" ? Number(seconds) : 0;
    }, [organizationPolicies]);

    const timeoutOptions: TimeoutOption[] = useMemo(() => {
        const options = [
            { label: "30 minutes", value: 30 * 60 },
            { label: "1 hour", value: 60 * 60 },
            { label: "3 hours", value: 3 * 60 * 60 },
            { label: "8 hours", value: 8 * 60 * 60 },
            { label: "No max timeout", value: 0 },
        ];
        if (!options.some((option) => option.value === currentTimeout)) {
            options.push({ label: formatSeconds(currentTimeout, "long"), value: currentTimeout });
        }

        return options;
    }, [currentTimeout]);

    const handleTimeoutChange = useCallback(
        async (valueString: string) => {
            const value = parseInt(valueString, 10);
            const params: PartialMessage<OrganizationPolicies> = {
                maximumEnvironmentTimeout: new Duration({
                    seconds: BigInt(value),
                    nanos: 0,
                }),
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: "Maximum timeout updated",
                });
            } catch (error) {
                console.error(error);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [updateOrganizationPolicies, toast],
    );

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-border-subtle px-5 py-4">
            <div className="flex flex-col gap-0.5">
                <Heading3 className="text-base">Maximum environment inactivity timeout</Heading3>
                <Text className="text-sm text-content-secondary">
                    Limit the maximum length of time that an environment can be in a running state without explicit user
                    input
                </Text>
            </div>
            <SkeletonBlock ready={!isLoading}>
                <Select
                    name="maximum-timeout"
                    value={currentTimeout.toString()}
                    onValueChange={handleTimeoutChange}
                    disabled={!editable}
                >
                    {/* Max width prevents select from stretching too wide while allowing content to fit comfortably */}
                    <SelectTrigger loading={false} className="max-w-[315px]">
                        <SelectValue placeholder="Select a timeout" />
                    </SelectTrigger>
                    <SelectContent className="w-[var(--radix-select-trigger-width)]">
                        {timeoutOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value.toString()}>
                                <div className="flex w-full items-center justify-between">{option.label}</div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </SkeletonBlock>
        </div>
    );
};
